import React from 'react';
import Icon, { IconName } from './Icon';

export interface GradientButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  loading?: boolean;
  disabled?: boolean;
  disableHoverEffects?: boolean;
  hoverStyle?: React.CSSProperties;
  icon?: IconName;
  iconPosition?: 'left' | 'right';
  customGradient?: string;
  className?: string;
}

const GradientButton: React.FC<GradientButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled = false,
  disableHoverEffects = false,
  hoverStyle,
  icon,
  iconPosition = 'left',
  customGradient,
  className = '',
  style,
  ...rest
}) => {
  // Size configurations
  const sizeConfig = {
    xxs: {
      padding: '4px 10px',
      fontSize: '11px',
      height: '26px',
      iconSize: '12px',
      gap: '4px'
    },
    xs: {
      padding: '5px 12px',
      fontSize: '12px',
      height: '30px',
      iconSize: '13px',
      gap: '5px'
    },
    sm: {
      padding: '6px 16px',
      fontSize: '13px',
      height: '32px',
      iconSize: '14px',
      gap: '6px'
    },
    md: {
      padding: '8px 20px',
      fontSize: '14px',
      height: '40px',
      iconSize: '16px',
      gap: '8px'
    },
    lg: {
      padding: '12px 24px',
      fontSize: '16px',
      height: '48px',
      iconSize: '18px',
      gap: '10px'
    },
    xl: {
      padding: '16px 32px',
      fontSize: '18px',
      height: '56px',
      iconSize: '20px',
      gap: '12px'
    }
  };

  // Variant gradient configurations
  const variantConfig = {
    primary: {
      gradient: 'linear-gradient(135deg, #08B8D3 0%, #045F6D 100%)',
      hoverGradient: 'linear-gradient(135deg, #0AA4C7 0%, #034A5A 100%)',
      color: '#ffffff',
      shadow: '0 4px 14px rgba(8, 184, 211, 0.3)'
    },
    secondary: {
      gradient: 'linear-gradient(135deg, #6b7280 0%, #374151 100%)',
      hoverGradient: 'linear-gradient(135deg, #5b6270 0%, #2d3748 100%)',
      color: '#ffffff',
      shadow: '0 4px 14px rgba(107, 114, 128, 0.3)'
    },
    success: {
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      hoverGradient: 'linear-gradient(135deg, #0ea574 0%, #047857 100%)',
      color: '#ffffff',
      shadow: '0 4px 14px rgba(16, 185, 129, 0.3)'
    },
    warning: {
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      hoverGradient: 'linear-gradient(135deg, #e8940b 0%, #c2670a 100%)',
      color: '#ffffff',
      shadow: '0 4px 14px rgba(245, 158, 11, 0.3)'
    },
    danger: {
      gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      hoverGradient: 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)',
      color: '#ffffff',
      shadow: '0 4px 14px rgba(239, 68, 68, 0.3)'
    },
    info: {
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      hoverGradient: 'linear-gradient(135deg, #3578f0 0%, #1d4ed8 100%)',
      color: '#ffffff',
      shadow: '0 4px 14px rgba(59, 130, 246, 0.3)'
    }
  };

  const currentSize = sizeConfig[size];
  const currentVariant = variantConfig[variant];

  const buttonStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: currentSize.gap,
    width: fullWidth ? '100%' : 'auto',
    height: currentSize.height,
    padding: currentSize.padding,
    fontSize: currentSize.fontSize,
    fontWeight: '600',
    fontFamily: 'Inter, system-ui, sans-serif',
    color: currentVariant.color,
    background: customGradient || currentVariant.gradient,
    border: 'none',
    borderRadius: '8px',
    boxShadow: disabled || loading ? 'none' : currentVariant.shadow,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease-in-out',
    outline: 'none',
    opacity: disabled || loading ? 0.6 : 1,
    overflow: 'hidden',
    ...style
  };

  const baseBackground = buttonStyle.background;
  const baseBoxShadow = buttonStyle.boxShadow;
  const baseColor = buttonStyle.color;
  const baseBorder = buttonStyle.border;

  const iconStyle: React.CSSProperties = {
    width: currentSize.iconSize,
    height: currentSize.iconSize,
    color: 'currentColor',
    flexShrink: 0
  };

  const loadingSpinnerStyle: React.CSSProperties = {
    width: currentSize.iconSize,
    height: currentSize.iconSize,
    border: '2px solid transparent',
    borderTop: '2px solid currentColor',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    flexShrink: 0
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading || disableHoverEffects) return;
    if (hoverStyle) {
      if (hoverStyle.background) e.currentTarget.style.background = String(hoverStyle.background);
      if (hoverStyle.boxShadow !== undefined) e.currentTarget.style.boxShadow = String(hoverStyle.boxShadow);
      if (hoverStyle.color) e.currentTarget.style.color = String(hoverStyle.color);
      if (hoverStyle.border) e.currentTarget.style.border = String(hoverStyle.border);
      e.currentTarget.style.transform = 'translateY(-1px)';
      return;
    }
    e.currentTarget.style.background = customGradient || currentVariant.hoverGradient;
    e.currentTarget.style.transform = 'translateY(-1px)';
    e.currentTarget.style.boxShadow = `0 6px 20px ${currentVariant.shadow.split('rgba')[1].split(')')[0]}, 0.4)`;
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading || disableHoverEffects) return;
    if (hoverStyle) {
      if (baseBackground) e.currentTarget.style.background = String(baseBackground);
      if (baseBoxShadow !== undefined) e.currentTarget.style.boxShadow = String(baseBoxShadow);
      if (baseColor) e.currentTarget.style.color = String(baseColor);
      if (baseBorder) e.currentTarget.style.border = String(baseBorder);
      e.currentTarget.style.transform = 'translateY(0)';
      return;
    }
    e.currentTarget.style.background = customGradient || currentVariant.gradient;
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = currentVariant.shadow;
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !loading && !disableHoverEffects) {
      e.currentTarget.style.transform = 'translateY(0)';
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !loading && !disableHoverEffects) {
      e.currentTarget.style.transform = 'translateY(-1px)';
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <button
        className={className}
        style={buttonStyle}
        disabled={disabled || loading}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        {...rest}
      >
        {/* Left Icon */}
        {icon && iconPosition === 'left' && !loading && (
          <Icon name={icon} style={iconStyle} />
        )}

        {/* Loading Spinner */}
        {loading && <div style={loadingSpinnerStyle} />}

        {/* Button Text */}
        {children}

        {/* Right Icon */}
        {icon && iconPosition === 'right' && !loading && (
          <Icon name={icon} style={iconStyle} />
        )}
      </button>
    </>
  );
};

export default GradientButton;
